import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button as ChakraButton,
  Flex,
  Grid,
  Icon,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IoLogoWhatsapp,
  IoPencilOutline,
  IoSearchSharp,
  IoTrashOutline,
} from 'react-icons/io5';
import { FiMail, FiUserCheck, FiUsers } from 'react-icons/fi';
import { MdOutlineBadge } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Form/Button';
import HeaderSideBar from '../components/HeaderSideBar';
import { useAuth } from '../contexts/AuthContext';
import { PermissionByIdDTO, RoleDTO } from '../dtos';
import api from '../services/api';
import { getFormatDate } from '../utils/date';

type FilterType = 'all' | 'name' | 'email' | 'cellphone' | 'role' | 'active' | 'birthdate';

export default function Permission() {
  const navigate = useNavigate();
  const toast = useToast();
  const { role, office } = useAuth();
  const cancelRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PermissionByIdDTO[]>([]);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [permissionToDeleteId, setPermissionToDeleteId] = useState('');
  const [selectFilter, setSelectFilter] = useState<FilterType>('all');
  const [filterValue, setFilterValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [roleFilter, setRoleFilter] = useState('');

  const canManage = role?.equipe_page > 1;

  const getPermissions = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(`/permission/office/${office?.id}`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to load permissions', error);
    } finally {
      setLoading(false);
    }
  }, [office?.id]);

  const getRoles = useCallback(async () => {
    try {
      const response = await api.get(`/role/office/${office?.id}`);
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to load roles', error);
    }
  }, [office?.id]);

  useEffect(() => {
    if (office?.id) {
      getPermissions();
      getRoles();
    }
  }, [getPermissions, getRoles, office?.id]);

  useEffect(() => {
    setFilterValue('');
    setActiveFilter('all');
    setRoleFilter('');
  }, [selectFilter]);

  const filteredPermissions = useMemo(() => {
    return data.filter(item => {
      const normalizedFilter = filterValue.trim().toLowerCase();

      switch (selectFilter) {
        case 'name':
          return !normalizedFilter || item.user?.name?.toLowerCase().includes(normalizedFilter);
        case 'email':
          return !normalizedFilter || item.user?.email?.toLowerCase().includes(normalizedFilter);
        case 'cellphone':
          return !normalizedFilter || item.user?.cellphone?.toLowerCase().includes(normalizedFilter);
        case 'role':
          return !roleFilter || item.role?.name === roleFilter;
        case 'active':
          if (activeFilter === 'active') return item.active === true;
          if (activeFilter === 'inactive') return item.active === false;
          return true;
        case 'birthdate':
          return (
            !normalizedFilter ||
            (item.user?.birthdate &&
              getFormatDate(new Date(item.user.birthdate), 'dd/MM/yyyy').includes(filterValue))
          );
        case 'all':
        default:
          return true;
      }
    });
  }, [activeFilter, data, filterValue, roleFilter, selectFilter]);

  const metrics = useMemo(() => {
    const total = data.length;
    const active = data.filter(item => item.active).length;
    const whatsapp = data.filter(item => item.user?.cellphone).length;
    const distinctRoles = new Set(data.map(item => item.role?.name).filter(Boolean)).size;

    return { total, active, whatsapp, distinctRoles };
  }, [data]);

  const handleUpdateActive = async (permissionId: string, currentActive: boolean) => {
    const updated = data.map(item =>
      item.id === permissionId ? { ...item, active: !currentActive } : item,
    );
    setData(updated);

    try {
      await api.put('/permission', {
        active: !currentActive,
        permissionId,
      });

      toast({
        title: 'Equipe atualizada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err: any) {
      getPermissions();
      toast({
        title:
          err?.response?.data?.message ||
          'Ocorreu um erro ao atualizar o status da equipe',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openDialog = (id: string) => {
    setPermissionToDeleteId(id);
    onOpen();
  };

  const deletePermission = async () => {
    setLoading(true);

    try {
      await api.delete(`/permission/${permissionToDeleteId}`);
      toast({
        title: 'Membro removido com sucesso',
        status: 'success',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
      setPermissionToDeleteId('');
      getPermissions();
    } catch (err: any) {
      toast({
        title:
          err?.response?.data?.message ||
          'Ocorreu um erro ao remover o membro da equipe',
        status: 'error',
        position: 'top-right',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <HeaderSideBar>
      <AlertDialog
        leastDestructiveRef={cancelRef}
        isOpen={isOpen}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx="12px">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deseja remover este membro?
            </AlertDialogHeader>
            <AlertDialogBody>
              Essa acao e irreversivel. O usuario perdera o vinculo com este gabinete.
            </AlertDialogBody>
            <AlertDialogFooter>
              <ChakraButton onClick={onClose}>Cancelar</ChakraButton>
              <ChakraButton colorScheme="red" ml={3} isLoading={loading} onClick={deletePermission}>
                Remover
              </ChakraButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Flex direction="column" gap="16px" pb="24px">
        <Box
          borderRadius="24px"
          px={{ base: '20px', md: '28px' }}
          py={{ base: '22px', md: '28px' }}
          bgGradient={`linear(135deg, ${office?.primary_color || '#0F766E'} 0%, ${
            office?.secondary_color || '#0F172A'
          } 100%)`}
          color="white"
          boxShadow="0 24px 80px rgba(15, 23, 42, 0.18)"
        >
          <Flex justify="space-between" gap="16px" direction={{ base: 'column', lg: 'row' }}>
            <Box maxW="680px">
              <Badge
                bg="rgba(255,255,255,0.14)"
                color="white"
                borderRadius="full"
                px="12px"
                py="4px"
                mb="12px"
              >
                Operacao da equipe
              </Badge>
              <Text fontSize={{ base: '24px', md: '32px' }} fontWeight="700">
                Pessoas, cargos e ativacao
                {loading && <Spinner color="white" ml="3" size="sm" />}
              </Text>
              <Text mt="8px" color="rgba(255,255,255,0.82)" maxW="560px">
                Visualize rapidamente quem esta ativo, qual cargo ocupa e quais contatos
                estao prontos para mobilizacao.
              </Text>
            </Box>

            {canManage && (
              <Button onClick={() => navigate('/equipe/registrar-equipe')} w={{ base: '100%', lg: '260px' }}>
                Cadastrar equipe
              </Button>
            )}
          </Flex>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="16px">
          {[
            { label: 'Integrantes', value: metrics.total, icon: FiUsers },
            { label: 'Ativos', value: metrics.active, icon: FiUserCheck },
            { label: 'Com WhatsApp', value: metrics.whatsapp, icon: IoLogoWhatsapp },
            { label: 'Cargos em uso', value: metrics.distinctRoles, icon: MdOutlineBadge },
          ].map(item => (
            <Box
              key={item.label}
              bg="white"
              borderRadius="20px"
              p="18px"
              border="1px solid #E2E8F0"
              boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
            >
              <Flex justify="space-between" align="center" mb="14px">
                <Text fontSize="13px" color="#64748B" fontWeight="600">
                  {item.label}
                </Text>
                <Icon as={item.icon} fontSize="18px" color="#0F766E" />
              </Flex>
              <Text fontSize="34px" lineHeight="1" fontWeight="700" color="#0F172A">
                {item.value}
              </Text>
            </Box>
          ))}
        </SimpleGrid>

        <Box
          bg="white"
          borderRadius="24px"
          p={{ base: '18px', md: '22px' }}
          border="1px solid #E2E8F0"
          boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
        >
          <Flex justify="space-between" gap="12px" direction={{ base: 'column', lg: 'row' }}>
            <Box>
              <Text fontSize="20px" fontWeight="700" color="#0F172A">
                Filtros da equipe
              </Text>
              <Text fontSize="13px" color="#64748B">
                Refaça o recorte por contato, cargo, status ou data de nascimento.
              </Text>
            </Box>

            <Flex gap="10px" direction={{ base: 'column', md: 'row' }}>
              <Select
                value={selectFilter}
                onChange={event => setSelectFilter(event.target.value as FilterType)}
                w={{ base: '100%', md: '190px' }}
                borderColor="#CBD5E1"
                bg="#F8FAFC"
              >
                <option value="all">Todos</option>
                <option value="name">Nome</option>
                <option value="email">E-mail</option>
                <option value="cellphone">Telefone</option>
                <option value="role">Cargo</option>
                <option value="active">Status</option>
                <option value="birthdate">Nascimento</option>
              </Select>

              {selectFilter === 'active' ? (
                <Select
                  value={activeFilter}
                  onChange={event => setActiveFilter(event.target.value as 'all' | 'active' | 'inactive')}
                  w={{ base: '100%', md: '180px' }}
                  borderColor="#CBD5E1"
                  bg="#F8FAFC"
                >
                  <option value="all">Todos</option>
                  <option value="active">Ativos</option>
                  <option value="inactive">Desativados</option>
                </Select>
              ) : selectFilter === 'role' ? (
                <Select
                  value={roleFilter}
                  onChange={event => setRoleFilter(event.target.value)}
                  w={{ base: '100%', md: '220px' }}
                  borderColor="#CBD5E1"
                  bg="#F8FAFC"
                >
                  <option value="">Todos os cargos</option>
                  {roles.map(currentRole => (
                    <option key={currentRole.id} value={currentRole.name}>
                      {currentRole.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Box position="relative">
                  <Input
                    value={filterValue}
                    onChange={event => setFilterValue(event.target.value)}
                    placeholder={selectFilter === 'birthdate' ? 'dd/mm/aaaa' : 'Buscar'}
                    pl="38px"
                    w={{ base: '100%', md: '260px' }}
                    borderColor="#CBD5E1"
                    bg="#F8FAFC"
                  />
                  <Icon
                    as={selectFilter === 'email' ? FiMail : IoSearchSharp}
                    position="absolute"
                    left="12px"
                    top="50%"
                    transform="translateY(-50%)"
                    color="#64748B"
                  />
                </Box>
              )}
            </Flex>
          </Flex>
        </Box>

        <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }} gap="16px">
          {filteredPermissions.length > 0 ? (
            filteredPermissions.map(permission => (
              <Box
                key={permission.id}
                bg="white"
                borderRadius="24px"
                p={{ base: '18px', md: '22px' }}
                border="1px solid #E2E8F0"
                boxShadow="0 20px 60px rgba(15, 23, 42, 0.08)"
              >
                <Flex justify="space-between" gap="12px" mb="16px">
                  <Flex direction="column" gap="6px">
                    <Flex align="center" gap="10px" wrap="wrap">
                      <Text fontSize="22px" fontWeight="700" color="#0F172A">
                        {permission.user?.name}
                      </Text>
                      <Badge
                        borderRadius="full"
                        px="10px"
                        py="4px"
                        bg={permission.active ? '#DCFCE7' : '#F1F5F9'}
                        color={permission.active ? '#166534' : '#475569'}
                      >
                        {permission.active ? 'Ativo' : 'Desativado'}
                      </Badge>
                    </Flex>
                    <Text fontSize="13px" color="#64748B">
                      {permission.role?.name || 'Sem cargo'}{' '}
                      {permission.user?.birthdate
                        ? `• nascimento em ${getFormatDate(new Date(permission.user.birthdate), 'dd/MM/yyyy')}`
                        : ''}
                    </Text>
                  </Flex>

                  <Flex align="center" gap="8px">
                    <Switch
                      isChecked={permission.active}
                      onChange={() => handleUpdateActive(permission.id, permission.active)}
                    />
                    {canManage && (
                      <>
                        <IconButton
                          aria-label="Editar membro"
                          borderRadius="14px"
                          bg="#F8FAFC"
                          icon={<Icon as={IoPencilOutline} color="#334155" fontSize="18px" />}
                          onClick={() => navigate(`/equipe/${permission.id}`)}
                        />
                        <IconButton
                          aria-label="Remover membro"
                          borderRadius="14px"
                          bg="#FFF1F2"
                          icon={<Icon as={IoTrashOutline} color="#BE123C" fontSize="18px" />}
                          onClick={() => openDialog(permission.id)}
                        />
                      </>
                    )}
                  </Flex>
                </Flex>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px">
                  <Box p="12px" borderRadius="16px" bg="#F8FAFC" border="1px solid #E2E8F0">
                    <Text fontSize="12px" color="#64748B">
                      E-mail
                    </Text>
                    <Text mt="4px" fontSize="14px" color="#0F172A" fontWeight="600">
                      {permission.user?.email || '-'}
                    </Text>
                  </Box>
                  <Box p="12px" borderRadius="16px" bg="#F8FAFC" border="1px solid #E2E8F0">
                    <Text fontSize="12px" color="#64748B">
                      Telefone
                    </Text>
                    <Flex mt="4px" align="center" gap="8px">
                      {permission.user?.cellphone ? (
                        <>
                          <a
                            href={`https://wa.me/55${permission.user.cellphone}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Icon as={IoLogoWhatsapp} color="#16A34A" fontSize="18px" />
                          </a>
                          <Text fontSize="14px" color="#0F172A" fontWeight="600">
                            {permission.user.cellphone}
                          </Text>
                        </>
                      ) : (
                        <Text fontSize="14px" color="#0F172A" fontWeight="600">
                          -
                        </Text>
                      )}
                    </Flex>
                  </Box>
                </SimpleGrid>
              </Box>
            ))
          ) : (
            <Box
              gridColumn="1 / -1"
              bg="white"
              borderRadius="24px"
              p="32px"
              border="1px dashed #CBD5E1"
              textAlign="center"
              color="#64748B"
            >
              Nenhum integrante encontrado com os filtros atuais.
            </Box>
          )}
        </Grid>
      </Flex>
    </HeaderSideBar>
  );
}
